import React, { useState, useEffect } from "react";
import { Image, Button, Spin, Typography, message } from "antd";
import {
  DownloadOutlined,
  PictureOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  fetchBenchmarkResultFile,
  BenchmarkResult,
} from "@/lib/api/resources/benchmark";
import { EmlViewerModal } from "./EmlViewer";

const { Text } = Typography;

export const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "bmp", "gif"];

export const ReferenceViewer: React.FC<{
  result: BenchmarkResult;
  textClassName?: string;
}> = ({ result, textClassName = "max-w-[200px] truncate" }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  const reference = result.inputReference || result.testCase?.name || "";
  const ext = reference.split(".").pop()?.toLowerCase() || "";
  const isImage = IMAGE_EXTENSIONS.includes(ext);
  const isEml = ext === "eml";
  const label = reference || "—";

  const loadFile = async (): Promise<string | null> => {
    if (blobUrl) return blobUrl;
    setLoading(true);
    try {
      const blob = await fetchBenchmarkResultFile(result.id);
      const url = URL.createObjectURL(blob);
      setBlob(blob);
      setBlobUrl(url);
      return url;
    } catch (e) {
      console.error("Failed to load reference file", e);
      message.error("Failed to load file. It may have been deleted.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (!result.inputReference) {
    return (
      <Text
        className={`font-mono text-xs block ${textClassName}`}
        title={reference}
      >
        {label}
      </Text>
    );
  }

  if (isImage) {
    return (
      <div className="flex items-center gap-2">
        <Text
          className={`font-mono text-xs block ${textClassName}`}
          title={reference}
        >
          {label}
        </Text>
        <Button
          type="text"
          size="small"
          icon={
            loading ? (
              <Spin size="small" />
            ) : (
              <PictureOutlined className="text-blue-500" />
            )
          }
          onClick={async () => {
            const url = await loadFile();
            if (url) setPreviewVisible(true);
          }}
          title="Preview image"
          className="p-0 h-auto flex items-center justify-center"
        />
        {blobUrl && (
          <Image
            src={blobUrl}
            style={{ display: "none" }}
            preview={{
              visible: previewVisible,
              src: blobUrl,
              onVisibleChange: (value) => setPreviewVisible(value),
            }}
          />
        )}
      </div>
    );
  }

  if (isEml) {
    return (
      <div className="flex items-center gap-2">
        <Text
          className={`font-mono text-xs block ${textClassName}`}
          title={reference}
        >
          {label}
        </Text>
        <Button
          type="text"
          size="small"
          icon={
            loading ? (
              <Spin size="small" />
            ) : (
              <FileTextOutlined className="text-blue-500" />
            )
          }
          onClick={async () => {
            const url = await loadFile();
            if (url) setPreviewVisible(true);
          }}
          title="Preview Email"
          className="p-0 h-auto flex items-center justify-center"
        />
        {blob && previewVisible && (
          <EmlViewerModal
            blob={blob}
            filename={label}
            visible={previewVisible}
            onClose={() => setPreviewVisible(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Text
        className={`font-mono text-xs block ${textClassName}`}
        title={reference}
      >
        {label}
      </Text>
      <Button
        type="text"
        size="small"
        icon={
          loading ? (
            <Spin size="small" />
          ) : (
            <DownloadOutlined className="text-gray-500" />
          )
        }
        onClick={async () => {
          const url = await loadFile();
          if (url) {
            const a = document.createElement("a");
            a.href = url;
            a.download = label;
            a.click();
          }
        }}
        title="Download file"
        className="p-0 h-auto flex items-center justify-center"
      />
    </div>
  );
};
